package ai.beakman.undercover.data

import ai.beakman.undercover.model.WordHint

/**
 * Theme texts in English (en). Game content for the EN locale.
 *
 * [NAMES]: themeId -> theme name. [WORDS]: wordId -> word + hint.
 * Keys are the SAME across all three languages (see [ThemeCatalog]).
 *
 * Hint style: VAGUE, "category"-like, short, and REUSED across 2-3 words of the
 * same theme (e.g. "Lives in the water" covers Dolphin + Shark). No hint may
 * uniquely identify its word; idiomatic English (not a literal PT translation).
 */
object ThemeStrings_en {
    val NAMES: Map<String, String> = mapOf(
        "animais" to "Animals",
        "comidas" to "Food",
        "profissoes" to "Jobs",
        "filmes" to "Movies",
        "lugares" to "Places",
        "esportes" to "Sports",
    )

    val WORDS: Map<String, WordHint> = mapOf(
        // animals
        "animais_1" to WordHint("Lion", "It has fur"),
        "animais_2" to WordHint("Elephant", "It's pretty big"),
        "animais_3" to WordHint("Dolphin", "Lives in the water"),
        "animais_4" to WordHint("Shark", "Lives in the water"),
        "animais_5" to WordHint("Eagle", "It can move fast"),
        "animais_6" to WordHint("Horse", "Gets around on four legs"),
        "animais_7" to WordHint("Dog", "You often see it at home"),
        "animais_8" to WordHint("Cat", "You often see it at home"),
        "animais_9" to WordHint("Panda", "It has striking colors"),
        "animais_10" to WordHint("Kangaroo", "Lives far from here"),
        "animais_11" to WordHint("Penguin", "Lives in a certain climate"),
        "animais_12" to WordHint("Giraffe", "Its body turns heads"),

        // food
        "comidas_1" to WordHint("Pizza", "Comes from abroad"),
        "comidas_2" to WordHint("Burger", "Comes from abroad"),
        "comidas_3" to WordHint("Sushi", "Comes from abroad"),
        "comidas_4" to WordHint("Mac & Cheese", "Feels like home"),
        "comidas_5" to WordHint("Lasagna", "Shows up at dinner"),
        "comidas_6" to WordHint("Meatloaf", "Feels like home"),
        "comidas_7" to WordHint("Hot Dog", "Usually street food"),
        "comidas_8" to WordHint("Ice Cream", "Served really cold"),
        "comidas_9" to WordHint("Cupcake", "Shows up at parties"),
        "comidas_10" to WordHint("Nuggets", "Usually street food"),
        "comidas_11" to WordHint("Barbecue", "Usually street food"),
        "comidas_12" to WordHint("Salad", "Feels light"),

        // jobs
        "profissoes_1" to WordHint("Doctor", "Deals with people"),
        "profissoes_2" to WordHint("Teacher", "Deals with people"),
        "profissoes_3" to WordHint("Firefighter", "A risky job"),
        "profissoes_4" to WordHint("Police Officer", "A risky job"),
        "profissoes_5" to WordHint("Engineer", "Needs a lot of study"),
        "profissoes_6" to WordHint("Lawyer", "Needs a lot of study"),
        "profissoes_7" to WordHint("Chef", "Works with their hands"),
        "profissoes_8" to WordHint("Pilot", "Works while traveling"),
        "profissoes_9" to WordHint("Dentist", "Deals with people"),
        "profissoes_10" to WordHint("Journalist", "Works with information"),
        "profissoes_11" to WordHint("Vet", "Deals with living things"),
        "profissoes_12" to WordHint("Architect", "Works creating things"),

        // movies
        "filmes_1" to WordHint("Titanic", "Was a huge hit"),
        "filmes_2" to WordHint("The Matrix", "Has a sequel"),
        "filmes_3" to WordHint("The Avengers", "Has a sequel"),
        "filmes_4" to WordHint("Frozen", "Fine to watch with kids"),
        "filmes_5" to WordHint("The Lord of the Rings", "It's quite long"),
        "filmes_6" to WordHint("Joker", "Was a huge hit"),
        "filmes_7" to WordHint("Avatar", "Known for its effects"),
        "filmes_8" to WordHint("Fast & Furious", "Has a sequel"),
        "filmes_9" to WordHint("Harry Potter", "Fine to watch with kids"),
        "filmes_10" to WordHint("Toy Story", "Fine to watch with kids"),
        "filmes_11" to WordHint("Jurassic Park", "Known for its effects"),
        "filmes_12" to WordHint("Rocky", "Has a sequel"),

        // places
        "lugares_1" to WordHint("Beach", "An outdoor spot"),
        "lugares_2" to WordHint("Mountain", "An outdoor spot"),
        "lugares_3" to WordHint("Forest", "An outdoor spot"),
        "lugares_4" to WordHint("Desert", "An outdoor spot"),
        "lugares_5" to WordHint("City", "Lots of people there"),
        "lugares_6" to WordHint("School", "You go every day"),
        "lugares_7" to WordHint("Hospital", "Nobody wants to stay long"),
        "lugares_8" to WordHint("Mall", "Lots of people there"),
        "lugares_9" to WordHint("Stadium", "Lots of people there"),
        "lugares_10" to WordHint("Airport", "A place you pass through"),
        "lugares_11" to WordHint("Museum", "A place to visit"),
        "lugares_12" to WordHint("Park", "An outdoor spot"),

        // sports
        "esportes_1" to WordHint("Soccer", "Played in a team"),
        "esportes_2" to WordHint("Basketball", "Played in a team"),
        "esportes_3" to WordHint("Volleyball", "Played in a team"),
        "esportes_4" to WordHint("Tennis", "Has its own gear"),
        "esportes_5" to WordHint("Swimming", "Takes a lot of breath"),
        "esportes_6" to WordHint("Surfing", "Depends on the weather"),
        "esportes_7" to WordHint("Skateboarding", "Has its own gear"),
        "esportes_8" to WordHint("Boxing", "Involves physical contact"),
        "esportes_9" to WordHint("Cycling", "Has its own gear"),
        "esportes_10" to WordHint("Running", "Takes a lot of breath"),
        "esportes_11" to WordHint("Golf", "Has its own gear"),
        "esportes_12" to WordHint("Handball", "Played in a team"),
    )
}
